import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Analyse workspace state — measurement tools + layer toggles. Session-only:
 * measurements are working notes, not saved products (exports come later
 * with the survey-data pipeline).
 */
export type AnalysisTool = 'none' | 'distance' | 'area';

export interface GeoPoint {
  lat: number;
  lon: number;
}

export interface AnalysisLayers {
  hillshade: boolean;
  contours: boolean;
  water: boolean;
  lithology: boolean;
}

/** An operator-registered live stream (MJPEG/HLS URL — see SPEC-0013 for
 *  how streams enter the system until the Hub grows a WebRTC relay). */
export interface LiveStream {
  id: string;
  name: string;
  url: string;
}

interface AnalysisStore {
  tool: AnalysisTool;
  points: GeoPoint[];
  layers: AnalysisLayers;
  streams: LiveStream[];
  /** Stream currently shown in the floating viewer (id) — session only. */
  openStreamId: string | null;
  setTool: (t: AnalysisTool) => void;
  addPoint: (p: GeoPoint) => void;
  undoPoint: () => void;
  clearPoints: () => void;
  setLayer: (k: keyof AnalysisLayers, v: boolean) => void;
  addStream: (name: string, url: string) => void;
  removeStream: (id: string) => void;
  openStream: (id: string | null) => void;
}

export const useAnalysis = create<AnalysisStore>()(
  persist(
    (set) => ({
      tool: 'none',
      points: [],
      layers: { hillshade: false, contours: false, water: false, lithology: false },
      streams: [],
      openStreamId: null,
      // Switching tools clears the sketch — a distance path is not a polygon.
      setTool: (tool) => set((s) => (s.tool === tool ? s : { tool, points: [] })),
      addPoint: (p) => set((s) => ({ points: [...s.points, p] })),
      undoPoint: () => set((s) => ({ points: s.points.slice(0, -1) })),
      clearPoints: () => set({ points: [] }),
      setLayer: (k, v) => set((s) => ({ layers: { ...s.layers, [k]: v } })),
      addStream: (name, url) =>
        set((s) => ({
          streams: [
            ...s.streams,
            { id: `st-${Math.random().toString(36).slice(2, 8)}`, name, url },
          ],
        })),
      removeStream: (id) =>
        set((s) => ({
          streams: s.streams.filter((x) => x.id !== id),
          openStreamId: s.openStreamId === id ? null : s.openStreamId,
        })),
      openStream: (openStreamId) => set({ openStreamId }),
    }),
    {
      name: 'fabricos.analysis.v1',
      // Streams + layer prefs persist; the measurement sketch does not.
      partialize: (s) => ({ streams: s.streams, layers: s.layers }),
    },
  ),
);

/** Haversine distance in metres. */
export function distM(a: GeoPoint, b: GeoPoint): number {
  const R = 6_371_000;
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const dφ = ((b.lat - a.lat) * Math.PI) / 180;
  const dλ = ((b.lon - a.lon) * Math.PI) / 180;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Total polyline length in metres. */
export function pathLengthM(pts: GeoPoint[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += distM(pts[i - 1]!, pts[i]!);
  return total;
}

/** Planar shoelace area on a local tangent plane — accurate for survey-scale
 *  polygons (well under ~10 km across). Returns m². */
export function polygonAreaM2(pts: GeoPoint[]): number {
  if (pts.length < 3) return 0;
  const lat0 = pts.reduce((sum, p) => sum + p.lat, 0) / pts.length;
  const mPerLat = 111_320;
  const mPerLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    sum += a.lon * mPerLon * b.lat * mPerLat - b.lon * mPerLon * a.lat * mPerLat;
  }
  return Math.abs(sum) / 2;
}

export function fmtDistance(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

export function fmtArea(m2: number): string {
  if (m2 >= 1_000_000) return `${(m2 / 1_000_000).toFixed(2)} km²`;
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} ha`;
  return `${Math.round(m2)} m²`;
}
